import React, { useContext } from 'react';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import PropTypes from 'prop-types';
import { Link, useRouteMatch } from 'react-router-dom';
import ViewIcon from 'assets/open_in_new.svg?react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import EditIcon from 'assets/edit_outlined.svg?react';
import RemoveIcon from 'assets/remove_circle_outlined.svg?react';
import FormattedNumber from 'components/FormattedNumber';
import { getAbbreviatedUnit } from 'helpers/unitsAbbreviation';
import { emptyValue } from 'screens/ProductList/helpers/tableConstants';
import { Context } from 'components/Store';
import ProductTypeDisplay from 'screens/ProductList/helpers/ProductTypeDisplay';
import { SET_SELECTED_PRODUCT } from 'reducers/reducer';
import ProductListCellQuantity from '../ProductListCellQuantity';
import '../ProductListTable/index.css';

const ProductActions = ({ product, onEdit, onRemove }) => {
  const amplitude = useContext(AmplitudeContext);
  const [{ organization }, dispatch] = useContext(Context);
  const {
    feProductListRemoveProduct,
    feProductListRemoveProductButtonByOrgId
  } = useFlags();
  const { url } = useRouteMatch();

  return (
    <div className="product-list-table__actions w-full flex items-center justify-end">
      <Link
        to={`${url}/${product.productId}/view`}
        className="flex flex-row product-list-table-action"
        onClick={() => {
          dispatch({
            type: SET_SELECTED_PRODUCT,
            payload: product
          });
          amplitude.sendEventToAmplitude(
            amplitude.events.epic.ProductList.viewProductDetails,
            { productId: product.productId }
          );
        }}
      >
        <ViewIcon
          className="product-list-table__action-icon mr-1"
          role="img"
          aria-label="view icon"
        />
        <span>View</span>
      </Link>
      <span
        className="product-list-table__action-separator"
        aria-hidden="true"
      />
      <div
        className="flex flex-row product-list-table-action"
        role="button"
        onClick={() => onEdit(product)}
        onKeyDown={event => {
          if (event.nativeEvent.key === 'Enter') {
            onEdit(product);
          }
        }}
        tabIndex={0}
        data-testid="btn-edit-product"
      >
        <EditIcon
          className="product-list-table__action-icon mr-1"
          role="img"
          aria-label="edit icon"
        />
        <span>Edit</span>
      </div>
      {feProductListRemoveProduct ||
      feProductListRemoveProductButtonByOrgId?.includes(organization?.id) ? (
        <>
          <span
            className="product-list-table__action-separator"
            aria-hidden="true"
          />
          <div
            className="flex flex-row product-list-table-action mr-2"
            role="button"
            onClick={() => onRemove(product)}
            onKeyDown={event => {
              if (event.nativeEvent.key === 'Enter') {
                onRemove(product);
              }
            }}
            tabIndex={0}
            data-testid="btn-remove-product"
          >
            <RemoveIcon
              className="product-list-table__action-icon mr-1"
              role="img"
              aria-label="remove icon"
            />
            <span>Remove</span>
          </div>
        </>
      ) : null}
    </div>
  );
};

const productShape = PropTypes.shape({
  trackingId: PropTypes.string,
  productId: PropTypes.string,
  productName: PropTypes.string,
  productType: PropTypes.string,
  available: PropTypes.number,
  stdPackageUnit: PropTypes.string,
  averagePricePerUnit: PropTypes.number,
  averagePricePerUnitUnit: PropTypes.string,
  manufacturerName: PropTypes.string
});

ProductActions.propTypes = {
  product: productShape.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

// One cell of the product list, picked by the table column's `field`.
const ProductListTableCell = ({ field, product, onEdit, onRemove }) => {
  let content;

  switch (field) {
    case 'productName':
      content = (
        <div className="-my-1">
          <div className="flex flex-col items-start text-sm">
            <span>{product.productName}</span>
            <span className="font-normal text-neutral-60 text-xs">
              {product?.manufacturerName}
            </span>
          </div>
        </div>
      );
      break;
    case 'productType':
      content = (
        <span className="product-list-table__type">
          <ProductTypeDisplay productType={product.productType} />
        </span>
      );
      break;
    case 'available':
      content = <ProductListCellQuantity product={product} />;
      break;
    case 'averagePricePerUnit':
      content = product.averagePricePerUnit ? (
        <>
          <FormattedNumber unit="usCurrency" roundedTo={2}>
            {product.averagePricePerUnit}
          </FormattedNumber>
          {' / '}
          {getAbbreviatedUnit(
            product.averagePricePerUnitUnit || product.stdPackageUnit
          )}
        </>
      ) : (
        emptyValue.none
      );
      break;
    case 'productId':
      content = (
        <ProductActions product={product} onEdit={onEdit} onRemove={onRemove} />
      );
      break;
    default:
      content = null;
  }

  return <>{content}</>;
};

ProductListTableCell.propTypes = {
  field: PropTypes.string.isRequired,
  product: productShape.isRequired,
  onEdit: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
};

export default ProductListTableCell;
